import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Bolt,
  ChevronRight,
  InfoOutlined,
  LabelOutlined,
  SearchOutlined,
  TrendingUp,
  Tune,
} from "@mui/icons-material";
import { PRODUCT_TRACKING_QUICK_FILTERS } from "../../constant/productTracking/productTrackingConfig";

const PRIMARY = "#6759FF";

export const EMPTY_QUICK_FILTERS = { values: {}, metrics: {} };

// Quick filters -> RULE_ITEMs on dimensions (chips / search: equals any of the values; metric: gte /
// lte on the row value).
const LIST_DELIMITERS = Object.fromEntries(
  PRODUCT_TRACKING_QUICK_FILTERS.filter((f) => f.listDelimiter).map((f) => [f.key, f.listDelimiter])
);

const valuesRule = (member, values) => {
  const d = LIST_DELIMITERS[member];
  if (!d) return { type: "RULE_ITEM", member, operator: "equals", values };
  // List dimension ("|S|M|"): any selected value in the list.
  return { type: "OR_RULE", or: values.map((v) => ({ type: "RULE_ITEM", member, operator: "contains", values: [`${d}${v}${d}`] })) };
};

export const quickFilterRuleItems = (quick) => [
  ...Object.entries(quick.values)
    .filter(([, values]) => values?.length)
    .map(([member, values]) => valuesRule(member, values)),
  ...Object.entries(quick.metrics)
    .filter(([, m]) => m?.value !== undefined && m.value !== "")
    .map(([member, m]) => ({ type: "RULE_ITEM", member, operator: m.op, values: [String(m.value)] })),
];

export const countQuickFilters = (quick) => quickFilterRuleItems(quick).length;

const groupSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.5,
  p: 1.5,
  border: "1px solid #E1E3E5",
  borderRadius: "8px",
  background: "#FAFAFB",
  minHeight: 64,
};

const GroupLabel = ({ icon: Icon, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 128, pr: 1.5, borderRight: "1px solid #E1E3E5", alignSelf: "stretch" }}>
    <Icon sx={{ fontSize: 18, color: "#6D7175" }} />
    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#202223" }}>{label}</Typography>
  </Box>
);

const chipSx = (active) => ({
  px: 1.5,
  py: 0.5,
  fontSize: "12px",
  textTransform: "none",
  borderRadius: "6px",
  border: "1px solid",
  borderColor: active ? PRIMARY : "#C9CCCF",
  background: active ? "rgba(103, 89, 255, 0.1)" : "#ffffff",
  color: active ? PRIMARY : "#202223",
  minWidth: 0,
});

const panelSwitchSx = { textTransform: "none", fontSize: "13px", borderColor: PRIMARY, color: PRIMARY };

// Filters panel, shown inline under the toolbar while the Filters button is on. Two views in the same
// panel: Quick filters (this component's draft, applied with Apply) and Advanced filters (the page's
// ComplexFilterWidget, passed in as `advancedWidget`), switched with the button at the top right.
const ProductTrackingFilters = ({
  open,
  onClose,
  mode,
  onModeChange,
  applied,
  onApply,
  optionsByKey,
  advancedCount,
  onClearAdvanced,
  advancedWidget,
}) => {
  const [draft, setDraft] = useState(applied);
  useEffect(() => {
    if (open) setDraft(applied);
  }, [open, applied]);

  const setValues = (key, values) => setDraft((d) => ({ ...d, values: { ...d.values, [key]: values } }));
  const setMetric = (key, patch) =>
    setDraft((d) => ({ ...d, metrics: { ...d.metrics, [key]: { op: "gte", value: "", ...d.metrics[key], ...patch } } }));
  const toggle = (key, value) => {
    const current = draft.values[key] ?? [];
    setValues(key, current.includes(value) ? current.filter((v) => v !== value) : [...current, value]);
  };

  const quickCount = countQuickFilters(draft);
  const summary =
    quickCount || advancedCount
      ? `${quickCount} quick filter${quickCount === 1 ? "" : "s"}${advancedCount ? ` + ${advancedCount} advanced` : ""} selected.`
      : "No filters selected. Showing all products.";

  const renderGroup = (f) => {
    if (f.kind === "chips") {
      const current = draft.values[f.key] ?? [];
      return (
        <Box key={f.key} sx={groupSx}>
          <GroupLabel icon={LabelOutlined} label={f.label} />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {f.options.map((o) => (
              <Button key={o.value} onClick={() => toggle(f.key, o.value)} sx={chipSx(current.includes(o.value))}>
                {o.label}
              </Button>
            ))}
          </Box>
        </Box>
      );
    }
    if (f.kind === "metric") {
      const m = draft.metrics[f.key] ?? { op: "gte", value: "" };
      return (
        <Box key={f.key} sx={groupSx}>
          <GroupLabel icon={TrendingUp} label={f.label} />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={m.op}
            onChange={(e, op) => op && setMetric(f.key, { op })}
            sx={{ "& .MuiToggleButton-root": { px: 1.5, py: 0.5, fontSize: "14px" }, "& .Mui-selected": { color: `${PRIMARY} !important`, background: "rgba(103, 89, 255, 0.1) !important" } }}
          >
            <ToggleButton value="gte">≥</ToggleButton>
            <ToggleButton value="lte">≤</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            type="number"
            placeholder="Enter value"
            value={m.value}
            onChange={(e) => setMetric(f.key, { value: e.target.value })}
            sx={{ flexGrow: 1, "& .MuiInputBase-input": { fontSize: "12px", py: "7px" } }}
          />
        </Box>
      );
    }
    return (
      <Box key={f.key} sx={groupSx}>
        <GroupLabel icon={SearchOutlined} label={f.label} />
        <Autocomplete
          multiple
          size="small"
          disableCloseOnSelect
          options={optionsByKey[f.key] ?? []}
          value={draft.values[f.key] ?? []}
          onChange={(e, values) => setValues(f.key, values)}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ fontSize: "12px" }}>
              <Checkbox size="small" checked={selected} sx={{ p: 0.5, mr: 1 }} />
              {option}
            </li>
          )}
          renderInput={(params) => <TextField {...params} placeholder="Search by name" />}
          ChipProps={{ size: "small" }}
          sx={{ flexGrow: 1, "& .MuiInputBase-root": { fontSize: "12px" } }}
        />
      </Box>
    );
  };

  if (!open) return null;

  // Advanced: the shared ComplexFilterWidget exactly as Product Analytics renders it (its own card,
  // border and Cancel / Apply), with only the switch back to Quick Filters above it.
  if (mode === "advanced") {
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Bolt />}
            endIcon={<ChevronRight />}
            onClick={() => onModeChange("quick")}
            sx={panelSwitchSx}
          >
            Quick Filters
          </Button>
        </Box>
        {advancedWidget}
      </Box>
    );
  }

  return (
    <Box sx={{ border: "1px solid #E1E3E5", borderRadius: "12px", mb: 2, background: "#ffffff", overflow: "hidden" }}>
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Bolt sx={{ color: PRIMARY }} />
            <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>Quick Filters</Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Tune />}
            endIcon={<ChevronRight />}
            onClick={() => onModeChange("advanced")}
            sx={panelSwitchSx}
          >
            Advanced filters{advancedCount ? ` (${advancedCount})` : ""}
          </Button>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
          {PRODUCT_TRACKING_QUICK_FILTERS.map(renderGroup)}
          <Box sx={{ ...groupSx, border: "1px dashed #B9B2FF", background: "#FBFAFF", alignItems: "center" }}>
            <Tune sx={{ color: PRIMARY }} />
            <Box>
              <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>Need another field?</Typography>
              <Typography sx={{ fontSize: "12px", color: "#6D7175" }}>
                Use <b>Advanced filters</b> for any other metric or attribute, and to combine rules with AND / OR.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2.5, py: 1.5, background: "#F6F6F7", borderTop: "1px solid #E1E3E5" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <InfoOutlined sx={{ fontSize: 16, color: "#6D7175" }} />
          <Typography sx={{ fontSize: "12px", color: "#6D7175" }}>{summary}</Typography>
          {(quickCount > 0 || advancedCount > 0) && (
            <Button
              size="small"
              onClick={() => {
                setDraft({ values: {}, metrics: {} });
                if (advancedCount) onClearAdvanced();
              }}
              sx={{ textTransform: "none", fontSize: "12px" }}
            >
              Clear all
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={onClose} sx={{ textTransform: "none", borderColor: PRIMARY, color: PRIMARY }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => onApply(draft)}
            sx={{ textTransform: "none", background: PRIMARY, "&:hover": { background: "#5246E0" } }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductTrackingFilters;
